import { PermissionGroup } from "shared/admin-permissions/permissionGroups";
import {
  type AdminPermissions,
  AdminUserPermissions,
  SocietyAdminPermissions,
} from "shared/admin-permissions/permissionNames";
import { PermissionScope } from "shared/admin-permissions/permissionScope";

export type PermissionDefinition = {
  /** The permission value itself, so a definition can be passed around alone. */
  name: AdminPermissions;
  /** Human readable name shown in the permission management UI. */
  displayName: string;
  /** What granting this permission actually lets the admin do. */
  description: string;
  group: PermissionGroup;
  scope: PermissionScope;
  /**
   * Admin UI pages gated by this permission, as Next.js route patterns.
   * `["*"]` means the permission applies to every page.
   */
  routes: string[];
  /**
   * API endpoints gated by this permission, as Next.js route patterns.
   * `["*"]` means the permission applies to every endpoint.
   */
  endpoints: string[];
};

/**
 * The single source of truth describing every permission: what it is called,
 * what it does, which feature area it belongs to, and which pages and endpoints
 * it gates. Keep this in step with the `useAdminPermissions` calls in
 * `admin/src/pages` and the `scope` entries in `admin/src/pages/api`.
 */
export const permissionDefinitions: Record<
  AdminPermissions,
  PermissionDefinition
> = {
  [AdminUserPermissions.super_admin]: {
    name: AdminUserPermissions.super_admin,
    displayName: "Super Admin",
    description:
      "Unrestricted access to every page, endpoint and society. Bypasses every other permission check, including the ones an admin has not been granted.",
    group: PermissionGroup.platform,
    scope: PermissionScope.global,
    routes: ["*"],
    endpoints: ["*"],
  },
  [AdminUserPermissions.manage_users]: {
    name: AdminUserPermissions.manage_users,
    displayName: "Manage Admin Users",
    description:
      "Create admin users, control which societies they belong to, and grant or revoke their permissions.",
    group: PermissionGroup.platform,
    scope: PermissionScope.global,
    routes: ["/admin/users"],
    endpoints: [
      "/api/v1/admin",
      "/api/v1/admin/[societyAdminId]",
      "/api/v1/admin/new",
    ],
  },
  [AdminUserPermissions.manage_venues]: {
    name: AdminUserPermissions.manage_venues,
    displayName: "Manage Venues",
    description:
      "Create and edit venues, venue brands and the rooms used when scheduling conference events.",
    group: PermissionGroup.platform,
    scope: PermissionScope.global,
    routes: ["/venues"],
    endpoints: [
      "/api/v1/venues",
      "/api/v1/venues/[venueId]",
      "/api/v1/venues/[venueId]/rooms",
      "/api/v1/venues/[venueId]/rooms/[roomId]",
      "/api/v1/venues/brands",
    ],
  },
  [AdminUserPermissions.kanban_board]: {
    name: AdminUserPermissions.kanban_board,
    displayName: "Task Management Board",
    description:
      "Open the internal staff task management kanban board and task list.",
    group: PermissionGroup.platform,
    scope: PermissionScope.global,
    routes: [
      "/task-management/kanban-board",
      "/task-management/task-list-view",
    ],
    endpoints: [],
  },
  [AdminUserPermissions.view_staff_notes]: {
    name: AdminUserPermissions.view_staff_notes,
    displayName: "Staff Notes",
    description:
      "Create and edit internal staff notes attached to a society. Reading notes only requires admin access.",
    group: PermissionGroup.staff,
    scope: PermissionScope.global,
    routes: [],
    endpoints: ["/api/v1/admin/notes"],
  },
  [AdminUserPermissions.post_staff_notes]: {
    name: AdminUserPermissions.post_staff_notes,
    displayName: "Edit Staff Notes",
    description:
      "Update and delete an individual staff note after it has been posted.",
    group: PermissionGroup.staff,
    scope: PermissionScope.global,
    routes: [],
    endpoints: ["/api/v1/admin/notes/[adminStaffNoteId]"],
  },
  [AdminUserPermissions.post_staff_timeline]: {
    name: AdminUserPermissions.post_staff_timeline,
    displayName: "Staff Timeline",
    description:
      "Create, update and delete entries on the internal staff timeline.",
    group: PermissionGroup.staff,
    scope: PermissionScope.global,
    routes: [],
    endpoints: [
      "/api/v1/admin/timeline",
      "/api/v1/admin/timeline/[adminStaffTimelineId]",
    ],
  },
  [SocietyAdminPermissions.profiles]: {
    name: SocietyAdminPermissions.profiles,
    displayName: "Profiles",
    description:
      "Search and list member profiles for the society. This is the entry point to every profile detail tab.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/profiles"],
    endpoints: ["/api/v1/society/[societyId]/profiles"],
  },
  [SocietyAdminPermissions.profiles_overview]: {
    name: SocietyAdminPermissions.profiles_overview,
    displayName: "Profile Overview Tab",
    description:
      "View the overview tab of a profile: name, status, membership summary and demographics.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/profiles/[profileId]/overview"],
    endpoints: [],
  },
  [SocietyAdminPermissions.profiles_membership]: {
    name: SocietyAdminPermissions.profiles_membership,
    displayName: "Profile Membership Tab",
    description:
      "View and manage the membership tab of a profile, including membership status history.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.profiles_accounting]: {
    name: SocietyAdminPermissions.profiles_accounting,
    displayName: "Profile Accounting Tab",
    description: "View a profile's invoices, payments and account balance.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/profiles/[profileId]/accounting"],
    endpoints: [],
  },
  [SocietyAdminPermissions.profiles_accounting_invoice]: {
    name: SocietyAdminPermissions.profiles_accounting_invoice,
    displayName: "Profile Invoice Detail",
    description:
      "Open an individual invoice from a profile's accounting tab and view its line items.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: [
      "/society/[societyId]/profiles/[profileId]/accounting/[invoiceId]",
    ],
    endpoints: [],
  },
  [SocietyAdminPermissions.profiles_education]: {
    name: SocietyAdminPermissions.profiles_education,
    displayName: "Profile Education Tab",
    description:
      "View and edit a profile's education, degrees and institution history.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/profiles/[profileId]/education"],
    endpoints: [],
  },
  [SocietyAdminPermissions.profiles_communication]: {
    name: SocietyAdminPermissions.profiles_communication,
    displayName: "Profile Communication Tab",
    description: "View the email and communication history sent to a profile.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/profiles/[profileId]/communication"],
    endpoints: [],
  },
  [SocietyAdminPermissions.profiles_files]: {
    name: SocietyAdminPermissions.profiles_files,
    displayName: "Profile Files Tab",
    description: "View, upload and remove files attached to a profile.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/profiles/[profileId]/files"],
    endpoints: [],
  },
  [SocietyAdminPermissions.profiles_tags]: {
    name: SocietyAdminPermissions.profiles_tags,
    displayName: "Profile Tags Tab",
    description: "View and edit the tags applied to a profile.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/profiles/[profileId]/tags"],
    endpoints: [],
  },
  [SocietyAdminPermissions.profiles_groups]: {
    name: SocietyAdminPermissions.profiles_groups,
    displayName: "Profile Groups Tab",
    description:
      "View and edit the groups and committees a profile belongs to.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/profiles/[profileId]/groups"],
    endpoints: [],
  },
  [SocietyAdminPermissions.profiles_notes]: {
    name: SocietyAdminPermissions.profiles_notes,
    displayName: "Profile Notes Tab",
    description: "Read the notes recorded against a profile.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/profiles/[profileId]/notes"],
    endpoints: [],
  },
  [SocietyAdminPermissions.manage_identity]: {
    name: SocietyAdminPermissions.manage_identity,
    displayName: "Profile Identity Tab",
    description:
      "View and repair a profile's logins: the identities and Cognito users behind each of its email addresses, and the actions that reconnect or disconnect them.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/profiles/[profileId]/identity"],
    endpoints: [
      "/api/v1/society/[societyId]/identity/cognito",
      "/api/v1/society/[societyId]/profiles/[profileId]/identity",
      "/api/v1/society/[societyId]/profiles/[profileId]/identity/[societyUserIdentityId]",
      "/api/v1/society/[societyId]/profiles/[profileId]/identity/login-details",
    ],
  },
  [SocietyAdminPermissions.profile_notes_manage]: {
    name: SocietyAdminPermissions.profile_notes_manage,
    displayName: "Manage Profile Notes",
    description:
      "Create and edit profile notes. Without it the notes tab is read only.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.profile_notes_delete]: {
    name: SocietyAdminPermissions.profile_notes_delete,
    displayName: "Delete Profile Notes",
    description: "Permanently delete a profile note.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.profile_delete]: {
    name: SocietyAdminPermissions.profile_delete,
    displayName: "Delete Profile",
    description:
      "Permanently delete a member profile and its related records. Destructive and not reversible.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.merge_profiles]: {
    name: SocietyAdminPermissions.merge_profiles,
    displayName: "Merge Profiles",
    description: "Merge two duplicate profiles into a single record.",
    group: PermissionGroup.profiles,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/profiles/merge"],
    endpoints: [],
  },
  [SocietyAdminPermissions.groups]: {
    name: SocietyAdminPermissions.groups,
    displayName: "Groups",
    description: "List the society's groups and committees.",
    group: PermissionGroup.groups,
    scope: PermissionScope.society,
    routes: [
      "/society/[societyId]/groups",
      "/society/[societyId]/groups/members",
    ],
    endpoints: [],
  },
  [SocietyAdminPermissions.group_details]: {
    name: SocietyAdminPermissions.group_details,
    displayName: "Group Detail",
    description: "Open an individual group and edit its settings and roster.",
    group: PermissionGroup.groups,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/groups/[groupId]"],
    endpoints: [],
  },
  [SocietyAdminPermissions.group_members]: {
    name: SocietyAdminPermissions.group_members,
    displayName: "Group Members",
    description:
      "Use the cross-group members report to see every group membership term in the society.",
    group: PermissionGroup.groups,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/groups/members"],
    endpoints: [],
  },
  [SocietyAdminPermissions.accounting]: {
    name: SocietyAdminPermissions.accounting,
    displayName: "Accounting",
    description:
      "Base access to the accounting area. Required alongside each individual accounting list permission.",
    group: PermissionGroup.accounting,
    scope: PermissionScope.society,
    routes: [
      "/society/[societyId]/accounting/deferement/length",
      "/society/[societyId]/accounting/deferement/range",
      "/society/[societyId]/accounting/discounts",
      "/society/[societyId]/accounting/invoices/items",
      "/society/[societyId]/accounting/payments",
      "/society/[societyId]/accounting/payments/items",
    ],
    endpoints: [],
  },
  [SocietyAdminPermissions.list_invoices]: {
    name: SocietyAdminPermissions.list_invoices,
    displayName: "Invoices List",
    description: "View the society-wide invoice list.",
    group: PermissionGroup.accounting,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/accounting/invoices"],
    endpoints: [],
  },
  [SocietyAdminPermissions.list_invoice_items]: {
    name: SocietyAdminPermissions.list_invoice_items,
    displayName: "Invoice Items List",
    description: "View the society-wide invoice line item list.",
    group: PermissionGroup.accounting,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/accounting/invoices/items"],
    endpoints: [],
  },
  [SocietyAdminPermissions.list_payments]: {
    name: SocietyAdminPermissions.list_payments,
    displayName: "Payments List",
    description: "View the society-wide payment list.",
    group: PermissionGroup.accounting,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/accounting/payments"],
    endpoints: [],
  },
  [SocietyAdminPermissions.list_payment_items]: {
    name: SocietyAdminPermissions.list_payment_items,
    displayName: "Payment Items List",
    description: "View the society-wide payment line item list.",
    group: PermissionGroup.accounting,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/accounting/payments/items"],
    endpoints: [],
  },
  [SocietyAdminPermissions.modify_payments]: {
    name: SocietyAdminPermissions.modify_payments,
    displayName: "Modify Payments",
    description:
      "Add, edit and void invoice items and payments. Controls the edit and delete actions on the invoice display, including the advanced edit of payment invoice items.",
    group: PermissionGroup.accounting,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [
      "/api/v1/society/[societyId]/profiles/[profileId]/accounting/payment/[paymentId]/item",
      "/api/v1/society/[societyId]/profiles/[profileId]/accounting/payment/[paymentId]/item/[paymentInvoiceItemId]",
    ],
  },
  [SocietyAdminPermissions.modify_payments_advanced]: {
    name: SocietyAdminPermissions.modify_payments_advanced,
    displayName: "Modify Payments Advanced",
    description:
      "Advanced permissions for modifying payments, includes editing and deleting.",
    group: PermissionGroup.accounting,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.basic_refunds]: {
    name: SocietyAdminPermissions.basic_refunds,
    displayName: "Basic Refunds",
    description:
      "Issue a full refund against an invoice without the broader modify payments permission.",
    group: PermissionGroup.accounting,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.discount]: {
    name: SocietyAdminPermissions.discount,
    displayName: "Discount Codes",
    description: "View and create discount codes for the society.",
    group: PermissionGroup.accounting,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/accounting/discounts"],
    endpoints: [],
  },
  [SocietyAdminPermissions.delete_discount_code]: {
    name: SocietyAdminPermissions.delete_discount_code,
    displayName: "Delete Discount Codes",
    description: "Permanently delete a discount code.",
    group: PermissionGroup.accounting,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [
      "/api/v1/society/[societyId]/accounting/discount/[discountCodeId]",
    ],
  },
  [SocietyAdminPermissions.deferement_range]: {
    name: SocietyAdminPermissions.deferement_range,
    displayName: "Deferments by Range",
    description: "View and manage deferments configured by date range.",
    group: PermissionGroup.accounting,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/accounting/deferement/range"],
    endpoints: [],
  },
  [SocietyAdminPermissions.deferement_length]: {
    name: SocietyAdminPermissions.deferement_length,
    displayName: "Deferments by Length",
    description: "View and manage deferments configured by length of time.",
    group: PermissionGroup.accounting,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/accounting/deferement/length"],
    endpoints: [],
  },
  [SocietyAdminPermissions.submissions]: {
    name: SocietyAdminPermissions.submissions,
    displayName: "Submissions",
    description:
      "Base access to the submissions area. Required alongside the definition and instance permissions.",
    group: PermissionGroup.submissions,
    scope: PermissionScope.society,
    routes: [
      "/society/[societyId]/submissions/definition",
      "/society/[societyId]/submissions/instance",
    ],
    endpoints: [],
  },
  [SocietyAdminPermissions.submission_definitions]: {
    name: SocietyAdminPermissions.submission_definitions,
    displayName: "Submission Definitions",
    description:
      "Create and edit the submission definitions that describe what members can submit.",
    group: PermissionGroup.submissions,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/submissions/definition"],
    endpoints: [],
  },
  [SocietyAdminPermissions.submission_instances]: {
    name: SocietyAdminPermissions.submission_instances,
    displayName: "Submission Instances",
    description:
      "View and manage the individual submissions members have made.",
    group: PermissionGroup.submissions,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/submissions/instance"],
    endpoints: [],
  },
  [SocietyAdminPermissions.submission_definition_advanced]: {
    name: SocietyAdminPermissions.submission_definition_advanced,
    displayName: "Advanced Submission Settings",
    description:
      "Edit the advanced configuration fields on a submission definition.",
    group: PermissionGroup.submissions,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.reviews]: {
    name: SocietyAdminPermissions.reviews,
    displayName: "Reviews",
    description: "Manage review sessions, reviewer assignments and recusals.",
    group: PermissionGroup.reviews,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/reviews"],
    endpoints: [],
  },
  [SocietyAdminPermissions.reviews_edit]: {
    name: SocietyAdminPermissions.reviews_edit,
    displayName: "Edit Review Sessions",
    description:
      "Change the configuration of an existing review session. Without it the review session form opens read only.",
    group: PermissionGroup.reviews,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.reviews_manual_assignment]: {
    name: SocietyAdminPermissions.reviews_manual_assignment,
    displayName: "Manual Assignment of Reviewers",
    description: "Manually assign and remove reviewers from assignments",
    group: PermissionGroup.reviews,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.review_session_member_delete]: {
    name: SocietyAdminPermissions.review_session_member_delete,
    displayName: "Delete Review Session Member",
    description: "Deletes a reviewer from a review session",
    group: PermissionGroup.reviews,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.conference]: {
    name: SocietyAdminPermissions.conference,
    displayName: "Conferences",
    description:
      "View and manage conferences, their schedules, events and event types.",
    group: PermissionGroup.conferences,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/conferences"],
    endpoints: [],
  },
  [SocietyAdminPermissions.delete_conference]: {
    name: SocietyAdminPermissions.delete_conference,
    displayName: "Delete Conference",
    description:
      "Permanently delete a conference and its schedule. Destructive and not reversible.",
    group: PermissionGroup.conferences,
    scope: PermissionScope.society,
    routes: [],
    endpoints: ["/api/v1/society/[societyId]/conference/[conferenceId]"],
  },
  [SocietyAdminPermissions.reports]: {
    name: SocietyAdminPermissions.reports,
    displayName: "Reports",
    description: "Run the society's saved reports and view their results.",
    group: PermissionGroup.reports,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/reports"],
    endpoints: [],
  },
  [SocietyAdminPermissions.report_edit]: {
    name: SocietyAdminPermissions.report_edit,
    displayName: "Edit Reports",
    description:
      "Change a report's columns, filters and saved definition from the report builder.",
    group: PermissionGroup.reports,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.report_config]: {
    name: SocietyAdminPermissions.report_config,
    displayName: "Configure Reports",
    description:
      "Change a report's underlying configuration, including its base table and sharing.",
    group: PermissionGroup.reports,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.forms]: {
    name: SocietyAdminPermissions.forms,
    displayName: "Forms",
    description:
      "Access the forms area. Also gates the Profiles entry in the society navigation.",
    group: PermissionGroup.forms,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.notifications]: {
    name: SocietyAdminPermissions.notifications,
    displayName: "Notifications",
    description: "Compose and send notifications to the society's members.",
    group: PermissionGroup.notifications,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/notifications/new"],
    endpoints: [],
  },
  [SocietyAdminPermissions.dashboard_page]: {
    name: SocietyAdminPermissions.dashboard_page,
    displayName: "Society Dashboard Pages",
    description:
      "Open the society's configured dashboard pages from the navigation.",
    group: PermissionGroup.dashboards,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.chatbot]: {
    name: SocietyAdminPermissions.chatbot,
    displayName: "Chatbot",
    description:
      "Configure the society's chatbot and review its conversations.",
    group: PermissionGroup.chatbot,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/chatbot"],
    endpoints: [],
  },
  [SocietyAdminPermissions.settings]: {
    name: SocietyAdminPermissions.settings,
    displayName: "Settings",
    description:
      "Base access to the society settings area. Required alongside each individual settings tab permission.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.settings_general]: {
    name: SocietyAdminPermissions.settings_general,
    displayName: "General Settings",
    description: "Edit the society's name, branding and general configuration.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/settings/general"],
    endpoints: [],
  },
  [SocietyAdminPermissions.custom_question_choices]: {
    name: SocietyAdminPermissions.custom_question_choices,
    displayName: "Custom Question Choices",
    description:
      "Manage the answer choices offered by the society's custom questions.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: [
      "/society/[societyId]/settings/choices",
      "/society/[societyId]/settings/questions/[questionName]",
    ],
    endpoints: [
      "/api/v1/society/[societyId]/choices",
      "/api/v1/society/[societyId]/choices/[questionName]",
      "/api/v1/society/[societyId]/choices/[questionName]/choice/[choiceId]",
      "/api/v1/society/[societyId]/choices/[questionName]/choice/bulk-create",
      "/api/v1/society/[societyId]/choices/[questionName]/choice/new",
    ],
  },
  [SocietyAdminPermissions.settings_forms]: {
    name: SocietyAdminPermissions.settings_forms,
    displayName: "Form Settings",
    description: "Configure the society's forms and their field definitions.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/settings/forms"],
    endpoints: [],
  },
  [SocietyAdminPermissions.settings_communication]: {
    name: SocietyAdminPermissions.settings_communication,
    displayName: "Communication Settings",
    description:
      "Configure email templates, senders and other communication settings.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/settings/communication"],
    endpoints: [],
  },
  [SocietyAdminPermissions.settings_routes]: {
    name: SocietyAdminPermissions.settings_routes,
    displayName: "Route Settings",
    description:
      "Configure the society's public app routes and the content rendered on them.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/settings/routes"],
    endpoints: [],
  },
  [SocietyAdminPermissions.settings_directories]: {
    name: SocietyAdminPermissions.settings_directories,
    displayName: "Directory Settings",
    description:
      "Create and edit member directories and the fields they expose.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: [
      "/society/[societyId]/settings/directories",
      "/society/[societyId]/settings/directories/[directoryId]/edit",
      "/society/[societyId]/settings/directories/new",
    ],
    endpoints: [
      "/api/v1/society/[societyId]/directory",
      "/api/v1/society/[societyId]/directory/[directoryId]",
    ],
  },
  [SocietyAdminPermissions.settings_society_files]: {
    name: SocietyAdminPermissions.settings_society_files,
    displayName: "Society Files",
    description: "Upload and manage files stored at the society level.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/settings/society-files"],
    endpoints: [],
  },
  [SocietyAdminPermissions.settings_products]: {
    name: SocietyAdminPermissions.settings_products,
    displayName: "Product Settings",
    description:
      "Create and edit the products, dues and fees the society sells.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/settings/products"],
    endpoints: [],
  },
  [SocietyAdminPermissions.settings_automated_tasks]: {
    name: SocietyAdminPermissions.settings_automated_tasks,
    displayName: "Automated Task Settings",
    description:
      "View the society's automated tasks, their recent runs and schedules; create, delete, run or restart them, and edit their schedules.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/settings/automated-tasks"],
    endpoints: ["/api/v1/society/[societyId]/automated-tasks"],
  },
  [SocietyAdminPermissions.settings_automated_tasks_authorize_net]: {
    name: SocietyAdminPermissions.settings_automated_tasks_authorize_net,
    displayName: "Automated Task: Authorize.Net Settlement",
    description:
      "Create, schedule, run, restart and delete the society's Authorize.Net settlement task under Settings > Automated Tasks. Also needs Automated Task Settings to open the page.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [
      "/api/v1/society/[societyId]/automated-tasks/task/authorize-net-settlement",
    ],
  },
  [SocietyAdminPermissions.settings_automated_tasks_profile_update]: {
    name: SocietyAdminPermissions.settings_automated_tasks_profile_update,
    displayName: "Automated Task: Profile Update",
    description:
      "Configure the profile update task (the workflow it runs, the state it enters and the dates that pick a profile up), and create, schedule, run, restart and delete it under Settings > Automated Tasks. Also needs Automated Task Settings to open the page.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [
      "/api/v1/society/[societyId]/automated-tasks/task/profile-update",
      "/api/v1/society/[societyId]/automated-tasks/task/profile-update/setting",
    ],
  },
  [SocietyAdminPermissions.settings_tags]: {
    name: SocietyAdminPermissions.settings_tags,
    displayName: "Tag Settings",
    description:
      "View the society's tags and how many people hold each, manually or dynamically, and create tags.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/settings/tags"],
    endpoints: ["/api/v1/society/[societyId]/tags/summary"],
  },
  [SocietyAdminPermissions.settings_advanced]: {
    name: SocietyAdminPermissions.settings_advanced,
    displayName: "Advanced Settings",
    description:
      "Edit raw society settings values. Intended for staff configuration work.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/settings/advanced"],
    endpoints: ["/api/v1/society/[societyId]/setting/[settingName]"],
  },
  [SocietyAdminPermissions.settings_aws]: {
    name: SocietyAdminPermissions.settings_aws,
    displayName: "AWS Settings",
    description:
      "Manage the society's S3 bucket, CORS rules and CloudFront distribution.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/settings/aws"],
    endpoints: [
      "/api/v1/society/[societyId]/aws",
      "/api/v1/society/[societyId]/aws/cloud-front",
      "/api/v1/society/[societyId]/aws/s3/cors",
    ],
  },
  [SocietyAdminPermissions.settings_reference]: {
    name: SocietyAdminPermissions.settings_reference,
    displayName: "Reference Tables",
    description:
      "View and edit the society's reference tables such as countries, statuses and institution types.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: [
      "/society/[societyId]/settings/reference",
      "/society/[societyId]/settings/reference/country",
      "/society/[societyId]/settings/reference/country-classification",
      "/society/[societyId]/settings/reference/country-classification-category",
      "/society/[societyId]/settings/reference/discount-category",
      "/society/[societyId]/settings/reference/email-template-type",
      "/society/[societyId]/settings/reference/institution-type",
      "/society/[societyId]/settings/reference/membership-status",
      "/society/[societyId]/settings/reference/profile-address-and-contact-information-type",
      "/society/[societyId]/settings/reference/profile-address-and-contact-information-use",
      "/society/[societyId]/settings/reference/profile-status",
    ],
    endpoints: [],
  },
  [SocietyAdminPermissions.settings_reference_detail]: {
    name: SocietyAdminPermissions.settings_reference_detail,
    displayName: "Reference Table Detail",
    description:
      "Open the generic reference table editor for any reference table by name.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/settings/reference/[refTableName]"],
    endpoints: [],
  },
  [SocietyAdminPermissions.settings_integrations]: {
    name: SocietyAdminPermissions.settings_integrations,
    displayName: "Integrations",
    description: "View the society's third party integration settings.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/settings/integrations"],
    endpoints: [],
  },
  [SocietyAdminPermissions.settings_integrations_billhighway]: {
    name: SocietyAdminPermissions.settings_integrations_billhighway,
    displayName: "BillHighway Integration",
    description:
      "Configure the BillHighway integration credentials and sync settings.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/settings/integrations/billhighway"],
    endpoints: ["/api/v1/society/[societyId]/setting/integrations/billhighway"],
  },
  [SocietyAdminPermissions.settings_integrations_higherlogic]: {
    name: SocietyAdminPermissions.settings_integrations_higherlogic,
    displayName: "Higher Logic Integration",
    description:
      "Configure the Higher Logic integration credentials and sync settings.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/settings/integrations/higherlogic"],
    endpoints: ["/api/v1/society/[societyId]/setting/integrations/higherlogic"],
  },
  [SocietyAdminPermissions.settings_integrations_memberplanet]: {
    name: SocietyAdminPermissions.settings_integrations_memberplanet,
    displayName: "MemberPlanet Integration",
    description:
      "Configure the MemberPlanet integration credentials and sync settings.",
    group: PermissionGroup.settings,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/settings/integrations/memberplanet"],
    endpoints: [
      "/api/v1/society/[societyId]/setting/integrations/memberplanet",
    ],
  },
  [SocietyAdminPermissions.workflow_builder]: {
    name: SocietyAdminPermissions.workflow_builder,
    displayName: "Workflow Builder",
    description: "Open the workflow builder and view the society's workflows.",
    group: PermissionGroup.workflows,
    scope: PermissionScope.society,
    routes: [
      "/society/[societyId]/workflow-builder",
      "/society/[societyId]/workflow-builder/[workflowId]",
    ],
    endpoints: [],
  },
  [SocietyAdminPermissions.workflow_builder_simple]: {
    name: SocietyAdminPermissions.workflow_builder_simple,
    displayName: "Simple Workflow Editing",
    description: "Create and edit workflows using the guided simple editor.",
    group: PermissionGroup.workflows,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.workflow_builder_advanced]: {
    name: SocietyAdminPermissions.workflow_builder_advanced,
    displayName: "Advanced Workflow Editing",
    description: "Create and edit workflows using the full node graph editor.",
    group: PermissionGroup.workflows,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
  [SocietyAdminPermissions.workflow_instance_manage]: {
    name: SocietyAdminPermissions.workflow_instance_manage,
    displayName: "Manage Workflow Instances",
    description:
      "Open the workflow instance management page, and delete eligible workflow instances with their related records on any environment.",
    group: PermissionGroup.workflows,
    scope: PermissionScope.society,
    routes: ["/society/[societyId]/workflow-instances"],
    endpoints: [
      "/api/v1/society/[societyId]/workflow-instances",
      "/api/v1/society/[societyId]/workflow/[workflowId]/instance/[workflowInstanceId]",
    ],
  },
  [SocietyAdminPermissions.workflow_instance_change_state]: {
    name: SocietyAdminPermissions.workflow_instance_change_state,
    displayName: "Change Workflow State",
    description:
      "Manually move a running workflow instance into a different state.",
    group: PermissionGroup.workflows,
    scope: PermissionScope.society,
    routes: [],
    endpoints: [],
  },
};
